import { Col, Row } from 'antd'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js'
import Flex from 'components/Flex'
import React, { useEffect, useRef, useState } from 'react'
import { Bar } from 'react-chartjs-2'
import ArrowIco from '../../../components/icons/ArrowIco'
import ExpendArrowIco from '../../../components/icons/ExpendArrowIco'
import { getStatusWithBG, keysToMapInActiveJobRoles } from './CommonFunction'
import GraphDetails from './GraphDetails'
import {
  CustomLegend,
  CustomLegendLabelRow,
  GraphTitle,
  ShadowBoxSubTitle,
  ShadowBoxTitle,
  StyledChartSecBox,
  StyledScrollBarButton,
  StyledScrollBox,
  StyledScrollBoxBody,
  StyledSpacer,
  StyledViewAllBtn,
} from './StyledComponent'

const verticalLinePlugin = {
  id: 'verticalLinePlugin', // Unique ID for the plugin
  afterDraw(chart) {
    const ctx = chart.ctx
    const yAxis = chart.scales.y // Access the Y-axis scale
    // Coordinates for the vertical line
    const lineX = yAxis.left + 13 // Start from the left edge of the Y-axis
    const lineYStart = yAxis.top // Top of the Y-axis
    const lineYEnd = yAxis.bottom - 90 // Bottom of the Y-axis

    ctx.save()
    ctx.beginPath()
    ctx.strokeStyle = '#00000040' // Line color
    ctx.lineWidth = 0.7 // Line thickness
    ctx.moveTo(lineX, lineYStart)
    ctx.lineTo(lineX, lineYEnd)
    ctx.stroke()
    ctx.restore()
  },
}

const cursorPlugin = {
  id: 'cursorPluginActiveJobRoles123', // Unique ID for ActiveJobRoles
  beforeEvent(chart, args, pluginOptions) {
    if (!chart || !args || !args.event || !args.event.native) return

    const event = args.event
    const rect = event.native.target?.getBoundingClientRect()
    if (!rect) return

    const x = event.native.clientX - rect.left
    const y = event.native.clientY - rect.top

    // Get axes positions
    const yAxis = chart.scales?.y
    const xAxis = chart.scales?.x
    if (!yAxis || !xAxis) return

    const isOverYAxis = x <= yAxis.right && x >= yAxis.left
    const isOverXAxis = y >= xAxis.top && y <= xAxis.bottom

    // Get elements under cursor
    const elements = chart.getElementsAtEventForMode(
      event,
      'nearest',
      { intersect: true },
      false
    )

    if (event.native.target) {
      event.native.target.style.cursor =
        isOverYAxis || isOverXAxis || elements?.length > 0
          ? 'pointer'
          : 'default'
    }
  },
}

const ActiveJobRoles = ({ data, state }) => {
  const { selectedCategory } = state
  const internship = selectedCategory === 'INTERNSHIP'
  const apprenticeship = selectedCategory === 'APPRENTICESHIP'
  const gigs = selectedCategory === 'GIGS'
  const activeJobRolesRef = useRef(null)
  const [isScrollable, setIsScrollable] = useState(false)
  const [selectedRole, setSelectedRole] = useState(null)
  const [viewAll, setViewAll] = useState(false)
  const chartRef = useRef(null)

  const chartOptionRef = useRef(null)
  const [statusDetail, setStatusDetail] = useState([])

  const totalStudents = Math.max(
    ...data.map(
      item =>
        Number(item.applied_student?.total_count) +
        Number(item.evaluation_in_progress?.total_count) +
        Number(item.rejected?.total_count) +
        Number(item.absent_status?.total_count) +
        Number(item.intern_to_offer?.total_count) +
        Number(item.offer_released?.total_count) +
        Number(item.offer_accepted?.total_count) +
        Number(item.offer_declined?.total_count)
    )
  )

  const datasets = [
    {
      label: 'Applied',
      backgroundColor: '#606060',
      data: data.map(item => item.applied_student?.total_count),
      barThickness: 11,
    },
    {
      label: 'In Eval. Process',
      backgroundColor: '#d3d3d3',
      data: data.map(item => item.evaluation_in_progress?.total_count),
      barThickness: 11,
    },
    {
      label: 'Rejected',
      backgroundColor: '#f39c12',
      data: data.map(item => item.rejected?.total_count),
      barThickness: 11,
    },

    {
      label: internship
        ? 'Internship Intent to Offer'
        : apprenticeship
        ? 'Apprenticeship Intent to Offer'
        : gigs
        ? 'Gig Intent to Offer'
        : 'Intent to Offer',
      backgroundColor: '#5dade2',
      data: data.map(item => item.intern_to_offer?.total_count),
      barThickness: 11,
    },
    {
      label: internship
        ? 'Internship Offer Released'
        : apprenticeship
        ? 'Apprenticeship Offer Released'
        : gigs
        ? 'Gig Offer Released'
        : 'Offer Released',
      backgroundColor: '#556BD9',
      data: data.map(item => item.offer_released?.total_count),
      barThickness: 11,
    },
    {
      label: internship
        ? 'Internship Offer Accepted'
        : apprenticeship
        ? 'Apprenticeship Offer Accepted'
        : gigs
        ? 'Gig Offer Accepted'
        : 'Offer Accepted',
      backgroundColor: '#27ae60',
      data: data.map(item => item.offer_accepted?.total_count),
      barThickness: 11,
    },
    {
      label: internship
        ? 'Internship Offer Declined'
        : apprenticeship
        ? 'Apprenticeship Offer Declined'
        : gigs
        ? 'Gig Offer Declined'
        : 'Offer Declined',
      backgroundColor: '#e74c3c',
      data: data.map(item => item.offer_declined?.total_count),
      barThickness: 11,
    },
    {
      label: 'Absent',
      backgroundColor: '#000000',
      data: data.map(item => item.absent_status?.total_count),
      barThickness: 11,
    },
  ]

  const chartData = {
    labels: data.map(item =>
      item.tittle.length > 12 ? `${item.tittle.slice(0, 12)}...` : item.tittle
    ),
    datasets,
  }

  const calculateTickInterval = maxValue => {
    return Math.ceil(maxValue / 15)
  }
  const stepSize =
    calculateTickInterval(totalStudents) <= 1
      ? 1
      : calculateTickInterval(totalStudents)

  const options = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest',
      intersect: true,
    },
    layout: {
      padding: {
        right: 9.5,
      },
    },
    scales: {
      x: {
        stacked: true,
        position: 'bottom',
        grid: {
          drawTicks: false,
          drawBorder: false,
        },
        ticks: {
          display: viewAll,
          stepSize: stepSize,
        },
        afterFit: function (scaleInstance) {
          scaleInstance.height -= 0
        },
      },
      y: {
        stacked: true,
        position: 'left',
        ticks: {
          color: function (context) {
            return context.tick.value === selectedRole?.value ? 'blue' : 'black'
          },
          font: {
            family: 'Arial',
          },
        },
        grid: {
          drawOnChartArea: false,
          drawTicks: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        displayColors: false,
        callbacks: {
          title: function (tooltipItems) {
            const index = tooltipItems[0]?.dataIndex
            return data[index]?.tittle || ''
          },
          label: () => null,
          beforeTitle: () => null,
          afterTitle: () => null,
          footer: () => null,
        },
      },
      verticalLinePlugin,
      cursorPlugin,
    },
  }

  const chartData2 = {
    datasets,
  }

  const options2 = {
    indexAxis: 'y', // Horizontal bar chart
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        right: 10,
      },
    },
    scales: {
      x: {
        afterFit: function (scaleInstance) {
          scaleInstance.height += 50
        },
        min: 0,
        max: Math.ceil(chartRef.current?.scales?.x?.end || 1),
        grid: {
          drawTicks: false,
        },
        ticks: {
          stepSize: stepSize,
        },
      },
      y: {
        afterFit: function (scaleInstance) {
          scaleInstance.width += chartRef.current.chartArea.left // Set custom width for the y-axis
        },
        grid: {
          drawTicks: false,
        },
      },
    },
    plugins: {
      tooltip: {
        enabled: false,
      },
      legend: {
        display: false,
      },
    },
  }

  const handleScrollUpButton = () => {
    if (activeJobRolesRef.current) {
      activeJobRolesRef.current.scrollBy({
        top: -100, // Scroll up by 100px
        behavior: 'smooth', // Smooth scrolling
      })
    }
  }

  const handleScrollDownButton = () => {
    if (activeJobRolesRef.current) {
      activeJobRolesRef.current.scrollBy({
        top: 100, // Scroll up by 100px
        behavior: 'smooth', // Smooth scrolling
      })
    }
  }

  const handleChartClick = event => {
    if (!chartRef.current || !event) return

    const {
      canvas,
      scales: { y },
    } = chartRef.current

    if (!canvas || !y) return

    const rect = canvas.getBoundingClientRect()
    if (!rect) return

    const xCoOr = event.clientX - rect.left
    const yCoOr = event.clientY - rect.top

    // Get clicked element
    const elements = chartRef.current.getElementsAtEventForMode(
      event,
      'nearest',
      { intersect: true },
      false
    )

    if (elements && elements.length > 0) {
      // If clicked on a bar
      const index = elements[0].index
      if (typeof index !== 'undefined' && data[index]) {
        setSelectedRole({
          ...data[index],
          value: index,
          label: data[index]?.tittle,
        })
      }
    } else {
      // Check if clicked on y-axis label
      const height = y.height / y.ticks.length
      for (let i = 0; i < y.ticks.length; i++) {
        if (
          xCoOr >= y.left &&
          xCoOr <= y.right &&
          yCoOr >= y.top + height * i &&
          yCoOr <= y.top + height + height * i &&
          data[i]
        ) {
          setSelectedRole({
            ...data[i],
            value: i,
            label: data[i]?.tittle,
          })
          break
        }
      }
    }
  }

  ChartJS.register(
    BarElement,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    cursorPlugin
  )

  useEffect(() => {
    const graphDetail =
      selectedRole &&
      keysToMapInActiveJobRoles?.map(key => {
        const { label, backgroundColor } = getStatusWithBG(
          key,
          internship
            ? 'Internship'
            : apprenticeship
            ? 'Apprenticeship'
            : gigs
            ? 'Gig'
            : ''
        ) // Get label and background color
        return {
          label, // Use predefined label from getStatusWithBG
          backgroundColor, // Assign background color
          total: selectedRole[key]?.total_count || '0',
          male: selectedRole[key]?.male || '0',
          female: selectedRole[key]?.female || '0',
          others: selectedRole[key]?.others || '0',
        }
      })

    setStatusDetail(graphDetail)
  }, [selectedRole])

  useEffect(() => {
    setSelectedRole(null)
  }, [state?.selectedCategory])

  const handleClearClick = () => {
    setSelectedRole(null)
  }

  useEffect(() => {
    const checkScrollAbility = () => {
      const element = activeJobRolesRef.current
      if (!element) return
      setIsScrollable(element.scrollHeight > element.clientHeight)
    }
    setTimeout(checkScrollAbility, 0)
  }, [data]) // Runs when `data` changes
  return (
    <div>
      <Flex centerVertically spaceBetween>
        <ShadowBoxTitle>
          {' '}
          Active{' '}
          {internship
            ? 'Internships'
            : apprenticeship
            ? 'Apprenticeship'
            : gigs
            ? 'Gigs'
            : 'Job Roles'}
        </ShadowBoxTitle>
        <StyledViewAllBtn
          text={viewAll ? 'Close' : 'View all'}
          onClick={() => setViewAll(!viewAll)}
          height={'32px'}
          width="95px"
          icon={viewAll ? false : <ExpendArrowIco />}
          disabled={data?.length < 10}
        />
      </Flex>

      <ShadowBoxSubTitle>
        Data below shows the active roles, the students and their status
      </ShadowBoxSubTitle>

      <div style={{ position: 'relative' }}>
        <StyledScrollBox ref={activeJobRolesRef} viewAll={viewAll}>
          <GraphTitle
            mBottom={35}
            position={'absolute'}
            bottom={'50px'}
            verticalOrientation
          >
            Job Roles
          </GraphTitle>
          <StyledScrollBoxBody
            isOverflow={chartData?.labels?.length > 7}
            labelsLength={chartData?.labels?.length}
            // ref={isScrollRef}
          >
            <Bar
              ref={chartRef}
              data={chartData}
              options={options}
              onClick={handleChartClick}
            />
          </StyledScrollBoxBody>
        </StyledScrollBox>
        {!viewAll && (
          <StyledChartSecBox isBackground={data?.length < 10}>
            <Bar ref={chartOptionRef} data={chartData2} options={options2} />
          </StyledChartSecBox>
        )}
        {!viewAll && isScrollable && (
          <div
            style={{
              position: 'absolute',
              display: 'flex',
              flexDirection: 'column',
              right: -5,
              bottom: 35,
            }}
          >
            <StyledSpacer />
            <StyledScrollBarButton
              id="scrollUpButton"
              onClick={handleScrollUpButton}
              rotate={180}
            >
              <ArrowIco active />
            </StyledScrollBarButton>
            <StyledSpacer />
            <StyledScrollBarButton
              id="scrollDownButton"
              onClick={handleScrollDownButton}
            >
              <ArrowIco />
            </StyledScrollBarButton>
            <StyledSpacer />
            <StyledSpacer />
          </div>
        )}
      </div>
      <GraphTitle mLeft={150} mBottom={30}>
        No. of students
      </GraphTitle>
      <Row>
        {chartData2?.datasets?.map((item, index) => (
          <Col key={index} style={{ marginRight: '20px' }}>
            <Row align={'middle'} gutter={5}>
              <Col>
                <CustomLegend color={item.backgroundColor} />
              </Col>
              <Col>
                <CustomLegendLabelRow
                  width={item?.label?.length > 15 ? '113px' : 'unset'}
                  weight={500}
                >
                  {item?.label}
                </CustomLegendLabelRow>
              </Col>
            </Row>
          </Col>
        ))}
      </Row>

      <GraphDetails
        data={selectedRole}
        setData={setSelectedRole}
        chartData={chartData2}
        statusDetail={statusDetail}
        handleClearClick={handleClearClick}
      />
    </div>
  )
}

export default ActiveJobRoles
